import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {
  ActivityIndicator,
  Row,
  Text,
  View,
} from '@unthinkable/react-core-components';
import {useTheme} from '@unthinkable/react-theme';
import {useDataFetchEvents} from '@unthinkable/react-data-handler';

import {HttpAuthService} from '../../../services';
import {routeLink} from '../../../constant';
import {encounterFields} from '../../../constant/encounter.fields';
import {action, entity} from '../../../constant/authorization';
import {Button} from '../../../components/button';
import buttonType from '../../../constant/buttonType';

import {PatientEncounterDetails} from '../screens/PatientEncounterDetail';
import {EncounterTimeline} from './EncounterTimeline';
import {
  tcmEncounterStyle,
  EncounterTimelineOpenCloseIcon,
  LoadingContainer,
} from './style/TcmEncounter.style';

import {useAuth} from '../../auth/hooks/useAuth';
import {useMediaQuery} from '../../auth/hooks/useMediaquery';
import {getPatientById} from '../service/patient.service';
import checkAuthorization from '../../../services/accessService';

const TCMEncounters = () => {
  const {patientId} = useParams();
  const {state} = useLocation();
  const {encounter: preselectedEncounter} = state || {};
  const [patient, setPatient] = useState(state?.patient || null);
  const [selectedEncounter, setSelectedEncounter] = useState(
    preselectedEncounter || null,
  );
  const [encounters, setEncounters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEncounterTimeline, setShowEncounterTimeline] = useState(false);

  const navigate = useNavigate();
  const {user} = useAuth();
  const {isDesktop} = useMediaQuery();
  const {DatePrevious, DateNext} = useTheme('icons');

  const eventSourceIds = [
    'transitional-event',
    'tcm-notes-status',
    'visitAttempt',
    'contactAttempts',
    'tcm-encounters',
    'form1823Task',
    'facilityAssignment',
    'initialTcmEligibility',
    'patientClassification',
    'MedicationTask',
  ];

  const fetchEncounters = async () => {
    setLoading(true);
    try {
      const response = await HttpAuthService.get(routeLink.getTCMEncounter, {
        params: {
          sort: {admissionDate: -1, createdAt: -1},
          fields: encounterFields,
          filter: JSON.stringify({patientId}),
        },
      });
      const data = response?.data || [];
      setEncounters(data);
      if (data.length) setSelectedEncounter(data[0]);
    } catch (err) {
      console.error('Error fetching encounters:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatient = async () => {
    if (patient) return;
    try {
      const response = await getPatientById(patientId);
      if (!response?.data?.length) {
        navigate('/patient');
      } else {
        setPatient(response?.data);
      }
    } catch {
      navigate('/patient');
    }
  };

  useEffect(() => {
    fetchPatient();
    fetchEncounters();
  }, []);

  useDataFetchEvents({eventSourceId: eventSourceIds}, fetchEncounters);

  const handleEncounterClick = encounter => {
    setSelectedEncounter(encounter);
    setShowEncounterTimeline(false);
  };

  const handleCreateEncounter = () => {
    navigate('create-new-encounter');
  };

  const renderNoData = (
    <>
      {checkAuthorization(user, entity.encounter, action.create) && (
        <Row style={{justifyContent: 'flex-end'}}>
          <Button
            key="create New Encounter"
            text="Create New Encounter"
            onPress={handleCreateEncounter}
            iconPosition="right"
            buttonType={buttonType.linkButtonWithBorder}
          />
        </Row>
      )}
      <LoadingContainer>
        <Text>No Data Found</Text>
      </LoadingContainer>
    </>
  );

  const renderContent = (
    <View style={tcmEncounterStyle}>
      {(!showEncounterTimeline || isDesktop) && (
        <PatientEncounterDetails
          patient={patient}
          encounter={selectedEncounter}
        />
      )}

      {!isDesktop && (
        <EncounterTimelineOpenCloseIcon
          src={showEncounterTimeline ? DateNext : DatePrevious}
          onClick={() => setShowEncounterTimeline(prev => !prev)}
          isOpen={showEncounterTimeline}
        />
      )}

      {(showEncounterTimeline || isDesktop) && (
        <EncounterTimeline
          encounters={encounters}
          selectedEncounter={selectedEncounter}
          handleEncounterClick={handleEncounterClick}
        />
      )}
    </View>
  );

  if (loading || !patient) {
    return (
      <LoadingContainer>
        <ActivityIndicator />
      </LoadingContainer>
    );
  }

  return selectedEncounter && patient ? renderContent : renderNoData;
};

export default TCMEncounters;
